import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';

import { BrandHeader } from '@/components/layout/BrandHeader';
import { useGoogleAuth } from '@/features/auth/useGoogleAuth';
import { useUser, useAuthReady } from '@/app/store';

import '@/styles/page-shell.css';
import '@/styles/login.css';

const ERR_MSG: Record<string, string> = {
  'auth/popup-closed-by-user':
    'Cerraste la ventana antes de completar el login.',
  'auth/popup-blocked':
    'El navegador bloqueó el popup. Permite popups y vuelve a intentar.',
  'auth/cancelled-popup-request':
    'Ya hay un intento de login en curso.',
  'auth/operation-not-allowed':
    'Google Sign-In no está habilitado en Firebase Console.',
  'auth/unauthorized-domain':
    'Este dominio no está autorizado en Firebase Console.',
  'auth/network-request-failed':
    'Sin conexión. Revisa tu red.',
};

export function LoginPage() {
  const user = useUser();
  const ready = useAuthReady();
  const { signIn } = useGoogleAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Ya logueado → directo al evento
  if (ready && user) return <Navigate to="/" replace />;

  const handleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      await signIn();
      // onAuthStateChanged actualiza el store y la Navigate de arriba dispara
    } catch (err) {
      const code = err instanceof FirebaseError ? err.code : 'unknown';
      setError(ERR_MSG[code] ?? `Error: ${code}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-shell">
      <div className="bg-grid" />
      <div className="bg-glow glow-top" />
      <div className="bg-glow glow-left" />

      <div className="login-wrap">
        <div className="login-card">
          <BrandHeader />
          <p className="login-pre">DATA CHALLENGE 2026</p>
          <h1 className="login-title">INICIA SESIÓN</h1>
          <p className="login-sub">
            Para participar en el ranking necesitas iniciar sesión con tu cuenta
            de Google. Es el único método permitido.
          </p>

          <button
            type="button"
            className="google-btn"
            onClick={handleSignIn}
            disabled={loading}
          >
            <GoogleLogo />
            {loading ? 'Conectando...' : 'CONTINUAR CON GOOGLE'}
          </button>

          {error && <div className="login-error">{error}</div>}

          <p className="login-fine">
            Al iniciar sesión aceptas que tu nombre y email se usen únicamente
            para el ranking del evento.
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleLogo() {
  // Logo oficial multi-color de Google
  return (
    <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}
